import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import BackendService from 'BackendService';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import Investor from '../../../models/Investor';
import ContactPortfolioFundCard from './ContactPortfolioFundCard';
import ContactPortfolioSectionCard from './ContactPortfolioSectionCard';

const CLOSED_PAGE_SIZE = 5;

export default function ContactPortfolioClosedFundsSection({
  contactId,
  completeFunds,
  navigate,
}: {
  contactId: string;
  completeFunds: number;
  navigate: NavigateFunction;
}) {
  const [closedFunds, setClosedFunds] = useState<Investor[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const closedLoadedRef = useRef(false);
  const [closedPage, setClosedPage] = useState(0);

  const loadClosedFunds = useCallback(() => {
    if (closedLoadedRef.current) return;
    closedLoadedRef.current = true;
    setClosedLoading(true);
    new BackendService()
      .getContactInvestments(contactId, { includeComplete: true })
      .then((all) => {
        const closed = all.filter((inv) => {
          const fund = inv.fundOpportunity as { status?: string } | undefined;
          return fund?.status === 'COMPLETE' || (inv as { isClosed?: boolean }).isClosed;
        });
        setClosedFunds(closed);
      })
      .catch(console.error)
      .finally(() => setClosedLoading(false));
  }, [contactId]);

  if (completeFunds <= 0) return null;

  const closedPageStart = closedPage * CLOSED_PAGE_SIZE;
  const closedFundsPage = closedFunds.slice(closedPageStart, closedPageStart + CLOSED_PAGE_SIZE);
  const closedTotalPages = Math.ceil(closedFunds.length / CLOSED_PAGE_SIZE);

  return (
    <ContactPortfolioSectionCard
      title={`Closed Funds${closedFunds.length > 0 ? ` (${closedFunds.length})` : ` (${completeFunds})`}`}
      icon={<LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      loading={closedLoading}
      defaultExpanded={false}
      action={
        <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>
          {closedFunds.length === 0 && !closedLoadedRef.current ? 'Click to load' : ''}
        </Typography>
      }
    >
      <Box
        ref={(el) => {
          if (el && !closedLoadedRef.current) loadClosedFunds();
        }}
      />

      {closedLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {!closedLoading && closedFunds.length === 0 && closedLoadedRef.current && (
        <Typography variant="body2" color="text.secondary">
          No closed fund data found.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {closedFundsPage.map((inv) => (
          <ContactPortfolioFundCard key={inv.id} investor={inv} navigate={navigate} closed />
        ))}
      </Box>

      {closedTotalPages > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
          <Button
            size="small"
            disabled={closedPage === 0}
            onClick={() => setClosedPage((p) => p - 1)}
            sx={{ textTransform: 'none' }}
          >
            ← Prev
          </Button>
          <Typography variant="caption" sx={{ alignSelf: 'center' }}>
            {closedPage + 1} / {closedTotalPages}
          </Typography>
          <Button
            size="small"
            disabled={closedPage >= closedTotalPages - 1}
            onClick={() => setClosedPage((p) => p + 1)}
            sx={{ textTransform: 'none' }}
          >
            Next →
          </Button>
        </Box>
      )}
    </ContactPortfolioSectionCard>
  );
}
